import React, { useMemo, useEffect, useRef } from "react";
import moment from "moment";
import { Typography } from "@progress/kendo-react-common";
import BasicDetailsSection from "./components/BasicDetailsSection";
import PackageCategorySection from "./components/PackageCategorySection";
import PackageOptionSection from "./components/PackageOptionSection";
import TransportsAndActivitiesSection from "./components/TransportsAndActivitiesSection";
import OtherSpecialServicesSection from "./components/OtherSpecialServicesSection";
import QuotationSummary from "./components/QuotationSummary";
import PricingAndSaveSection from "./components/PricingAndSaveSection";
import { Form, FormElement, Field } from "@progress/kendo-react-form";
import "./b2bmodule.scss";
import { insertB2BPackageQuotation, updateB2BPackageQuotation, findByB2BCustomQuotation } from "./services/b2bQuotation.services";
import { useAppDispatch } from "../../app/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { getLeadByID } from "../lead/services/lead.services";
import { getInquiryByID } from "../inquiry/services/inquiry.services";
import CustomerHeader from "./components/CustomerHeader";
import { getAllActiveItinerary } from "../itinerary/services/itineary.services";
import { getCityBySectorID } from "../city/services/city.services";
import { LoadingPanel } from "../../components/layout/Loading";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import ShadowCard from "../../components/common/ShadowCard";
import B2BTourItinerarySection from "./components/B2BTourItinerarySection";
import IconComponent from "../../components/common/IconComponent";
import { FaClipboardList } from "react-icons/fa";
import defaultitineraryimage from "../../assets/Images/default-itinerary-image.jpg";

// ─────────────────────────────────────────────────────────────────────────────
// initialValues
// ─────────────────────────────────────────────────────────────────────────────
const initialValues = {
  LeadID: 0,
  InquiryID: 0,
  Initial: "",
  FirstName: "",
  LastName: "",
  EmailID: "",
  MobileNo: "",
  LeadSourceID: null,
  QuotationID: 0,
  QuotationReference: "",
  TripDestinationID: 0,
  Nights: 3,
  TripStartDate: new Date(),
  TripEndDate: moment().add(3, "days").toDate(),
  Adults: 2,
  Children: 0,
  Infants: 0,
  PackageOptions: [
    {
      OptionName: "Deluxe Package",
      AccommodationsTotal: 0,
      TransportsTotal: 0,
      ActivityTotal: 0,
      ExtraServicesTotal: 0,
      OverallTotal: 0,
      MarkupType: "Amount",
      MarkupValue: 0,
      MarkupAmount: 0,
      GrandTotal: 0,
      Hotels: [
        {
          HotelID: 0,
          MealPlanID: 0,
          RoomTypeID: 0,
          Nights: [],
          NoOfRooms: 1,
          Adults: 2,
          AWEB: 0,
          CWEB: 0,
          CNB: 0,
          Comment: "",
          NightPrices: []
        }
      ],
      HotelSpecialInclusions: []
    }
  ],
  TransportsAndActivities: {
    // SameCabTypeForAll: true,
    ServiceGroups: [],
    // ✅ ExtraTransportationServices lives here in this version of the file
    ExtraTransportationServices: [],
  },
  OtherSpecialServices: [],
  Inclusion: "",
  Exclusion: "",
  ImportantNotes: "",
  TermsConditions: "",
  Itinary: [
    { DayNo: 1, PlaceID: null, ItinaryID: null, Description: "", ItineraryImage: defaultitineraryimage }
  ]
};

// ─────────────────────────────────────────────────────────────────────────────
// computeTransportTotalsFromGetter
// Reads every live nested FieldArray value directly via valueGetter,
// bypassing the stale `values` snapshot that Kendo Form provides.
// NOTE: ExtraTransportationServices path = TransportsAndActivities.ExtraTransportationServices
// ─────────────────────────────────────────────────────────────────────────────
const computeTransportTotalsFromGetter = (valueGetter: (f: string) => any) => {
  let transportTotal = 0;
  let activityTotal = 0;

  // ── ServiceGroups ─────────────────────────────────────────────────────────
  const serviceGroups: any[] =
    valueGetter("TransportsAndActivities.ServiceGroups") || [];

  serviceGroups.forEach((group: any, gIdx: number) => {
    const services: any[] =
      valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services`) ||
      group?.Services || [];

    services.forEach((srv: any, sIdx: number) => {
      const subservices: any[] =
        valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices`) ||
        srv?.Subservices || [];

      let svcTotal = 0;
      subservices.forEach((sub: any, subIdx: number) => {
        const qty = parseFloat(
          valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].Qty`) ??
          sub?.Qty
        ) || 0;

        const dailyPrices: any[] =
          valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].DailyPrices`) ||
          sub?.DailyPrices || [];

        dailyPrices.forEach((dp: any, dpIdx: number) => {
          const livePrice =
            valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].DailyPrices[${dpIdx}].GivenPrice`) ??
            dp?.GivenPrice;
          svcTotal += qty * (parseFloat(livePrice) || 0);
        });
      });

      const type = (srv.Type || "").toUpperCase();
      if (type === "ACTIVITY") activityTotal += svcTotal;
      else transportTotal += svcTotal;
    });
  });

  // ── ExtraTransportationServices (nested under TransportsAndActivities) ────
  const extraServices: any[] =
    valueGetter("TransportsAndActivities.ExtraTransportationServices") || [];
  const extraTransTotal = extraServices.reduce((acc: number, e: any, eIdx: number) => {
    const livePrice =
      valueGetter(`TransportsAndActivities.ExtraTransportationServices[${eIdx}].TotalPrice`) ??
      e?.TotalPrice;
    return acc + (parseFloat(livePrice) || 0);
  }, 0);

  // ── OtherSpecialServices (root level) ────────────────────────────────────
  const otherSpecial: any[] = valueGetter("OtherSpecialServices") || [];
  const otherSpecialTotal = otherSpecial.reduce((acc: number, s: any, sIdx: number) => {
    const livePrice =
      valueGetter(`OtherSpecialServices[${sIdx}].TotalPrice`) ?? s?.TotalPrice;
    return acc + (parseFloat(livePrice) || 0);
  }, 0);

  return { transportTotal, activityTotal, extraTransTotal, otherSpecialTotal };
};

// ─────────────────────────────────────────────────────────────────────────────
// calculateQuotationTotals
// Accepts optional valueGetter for live reads (used during form rendering).
// Falls back to pure snapshot calculation (used in handleSubmit).
// ─────────────────────────────────────────────────────────────────────────────
export const calculateQuotationTotals = (values: any, valueGetter?: (f: string) => any) => {
  if (!values) return {
    packageOptionsTotals: [],
    globalTransportTotal: 0,
    globalActivityTotal: 0,
    globalExtraTotal: 0
  };

  let transportTotal = 0;
  let activityTotal = 0;
  let extraTransTotal = 0;
  let otherSpecialTotal = 0;

  if (valueGetter) {
    // ── Live path: read directly from Kendo field registry ──────────────────
    const t = computeTransportTotalsFromGetter(valueGetter);
    transportTotal = t.transportTotal;
    activityTotal = t.activityTotal;
    extraTransTotal = t.extraTransTotal;
    otherSpecialTotal = t.otherSpecialTotal;
  } else {
    // ── Snapshot fallback (handleSubmit) ─────────────────────────────────────
    (values.TransportsAndActivities?.ServiceGroups || []).forEach((group: any) => {
      (group?.Services || []).forEach((srv: any) => {
        let svcTotal = 0;
        (srv?.Subservices || []).forEach((sub: any) => {
          const qty = parseFloat(sub?.Qty) || 0;
          (sub?.DailyPrices || []).forEach((dp: any) => {
            svcTotal += qty * (parseFloat(dp?.GivenPrice) || 0);
          });
        });
        if ((srv.Type || "").toUpperCase() === "ACTIVITY") activityTotal += svcTotal;
        else transportTotal += svcTotal;
      });
    });

    // ExtraTransportationServices is nested under TransportsAndActivities
    extraTransTotal = (values.TransportsAndActivities?.ExtraTransportationServices || []).reduce(
      (acc: number, e: any) => acc + (parseFloat(e?.TotalPrice) || 0), 0
    );
    otherSpecialTotal = (values.OtherSpecialServices || []).reduce(
      (acc: number, s: any) => acc + (parseFloat(s?.TotalPrice) || 0), 0
    );
  }

  const totalExtras = extraTransTotal + otherSpecialTotal;

  const packageOptionsTotals = (values.PackageOptions || []).map((opt: any, idx: number) => {
    if (!opt) return {
      AccommodationsTotal: 0, TransportsTotal: 0, ActivityTotal: 0,
      ExtraServicesTotal: 0, OverallTotal: 0, MarkupAmount: 0, GrandTotal: 0
    };

    // ── Hotel costs ──────────────────────────────────────────────────────────
    let hotelCost = 0;
    (opt.Hotels || []).forEach((h: any, hIdx: number) => {
      if (!h) return;
      const noOfRooms = parseFloat(
        valueGetter
          ? (valueGetter(`PackageOptions[${idx}].Hotels[${hIdx}].NoOfRooms`) ?? h.NoOfRooms)
          : h.NoOfRooms
      ) || 1;

      (h.NightPrices || []).forEach((p: any, pIdx: number) => {
        const livePrice = valueGetter
          ? (valueGetter(`PackageOptions[${idx}].Hotels[${hIdx}].NightPrices[${pIdx}].GivenPrice`) ?? p?.GivenPrice)
          : p?.GivenPrice;
        hotelCost += noOfRooms * (parseFloat(livePrice) || 0);
      });
    });

    const inclusionsCost = (opt.HotelSpecialInclusions || []).reduce(
      (acc: number, i: any, iIdx: number) => {
        const livePrice = valueGetter
          ? (valueGetter(`PackageOptions[${idx}].HotelSpecialInclusions[${iIdx}].TotalPrice`) ?? i?.TotalPrice)
          : i?.TotalPrice;
        return acc + (parseFloat(livePrice) || 0);
      }, 0
    );

    const accTotal = hotelCost + inclusionsCost;
    const overallTotal = accTotal + transportTotal + activityTotal + totalExtras;

    // ── Markup — reads from opt.* (where PricingAndSaveSection writes) ──
    const liveMarkupType = valueGetter
      ? (valueGetter(`PackageOptions[${idx}].MarkupType`) ?? opt.MarkupType ?? "Amount")
      : (opt.MarkupType || "Amount");

    const liveMarkupValue = parseFloat(
      valueGetter
        ? (valueGetter(`PackageOptions[${idx}].MarkupValue`) ?? opt.MarkupValue ?? 0)
        : (opt.MarkupValue ?? 0)
    ) || 0;

    const markupAmount = liveMarkupType === "Percentage"
      ? (overallTotal * liveMarkupValue / 100)
      : liveMarkupValue;

    const grandTotal = overallTotal + markupAmount;

    return {
      AccommodationsTotal: accTotal,
      TransportsTotal: transportTotal,
      ActivityTotal: activityTotal,
      ExtraServicesTotal: totalExtras,
      OverallTotal: overallTotal,
      MarkupAmount: markupAmount,
      GrandTotal: grandTotal
    };
  });

  return {
    packageOptionsTotals,
    globalTransportTotal: transportTotal,
    globalActivityTotal: activityTotal,
    globalExtraTotal: totalExtras
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// QuotationFormBody
// ─────────────────────────────────────────────────────────────────────────────
const QuotationFormBody = (formRenderProps: any) => {
  const values = formRenderProps.valueGetter("");
  const onFormChange = formRenderProps.onChange;
  const valueGetter = formRenderProps.valueGetter;

  const startDate = valueGetter("TripStartDate");
  const nights = Number(valueGetter("Nights")) || 0;

  // ✅ SYNC TRIP END DATE: Automatically update TripEndDate when StartDate or Nights changes
  useEffect(() => {
    if (startDate) {
      const newEndDate = moment(startDate).add(nights, 'days').toDate();
      const currentEndDate = valueGetter("TripEndDate");
      const fmtNew = moment(newEndDate).format("YYYY-MM-DD");
      const fmtCur = currentEndDate ? moment(currentEndDate).format("YYYY-MM-DD") : "";

      if (fmtNew !== fmtCur) {
        onFormChange("TripEndDate", { value: newEndDate });
      }
    }
  }, [startDate, nights, onFormChange, valueGetter]);

  // ✅ AUTOMATED CITY FETCHING: Fetch cities based on the selected Destination/Sector ID
  const tripSectorID = valueGetter("TripDestinationID");
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (tripSectorID) {
      dispatch(getCityBySectorID(tripSectorID));
    }
  }, [tripSectorID, dispatch]);

  // ✅ Removed useMemo to ensure totals are recomputed on EVERY render for live updates
  const totals = calculateQuotationTotals(values, valueGetter);

  // ── Sync computed totals back into form state ─────────────────────────────
  // ── Centralized Synchronization (Pax & Totals) ───────────────────────────
  const prevSyncRef = React.useRef("");
  useEffect(() => {
    if (!values?.PackageOptions) return;

    // 1. Create a comprehensive fingerprint of all syncable inputs/outputs
    const syncHash = JSON.stringify({
      totals: totals.packageOptionsTotals,
      tripPax: { a: values.Adults, c: values.Children, i: values.Infants },
      hotelPax: (values.PackageOptions || []).map((opt: any) =>
        (opt.Hotels || []).map((h: any) => ({ a: h.Adults, c: h.CWEB, i: h.Infants, aw: h.AWEB, cn: h.CNB }))
      )
    });

    if (prevSyncRef.current === syncHash) return;
    prevSyncRef.current = syncHash;

    // 2. Perform coordinated updates
    (totals.packageOptionsTotals || []).forEach((t: any, idx: number) => {
      const opt = values.PackageOptions[idx];
      if (!opt) return;

      // A. Write totals fields back to form state
      const totalFields: Array<[string, any]> = [
        [`PackageOptions[${idx}].AccommodationsTotal`, t.AccommodationsTotal],
        [`PackageOptions[${idx}].TransportsTotal`, t.TransportsTotal],
        [`PackageOptions[${idx}].ActivityTotal`, t.ActivityTotal],
        [`PackageOptions[${idx}].ExtraServicesTotal`, t.ExtraServicesTotal],
        [`PackageOptions[${idx}].OverallTotal`, t.OverallTotal],
        [`PackageOptions[${idx}].MarkupAmount`, t.MarkupAmount],
        [`PackageOptions[${idx}].GrandTotal`, t.GrandTotal],
      ];

      totalFields.forEach(([path, val]) => {
        if (valueGetter(path) !== val) onFormChange(path, { value: val });
      });

      // B. Propagate master trip Pax to individual hotels if they haven't deviated
      (opt.Hotels || []).forEach((h: any, hIdx: number) => {
        const pathPrefix = `PackageOptions[${idx}].Hotels[${hIdx}]`;
        const needsPaxSync =
          Number(h.Adults) !== Number(values.Adults) ||
          Number(h.CWEB) !== Number(values.Children) ||
          Number(h.Infants) !== Number(values.Infants) ||
          Number(h.AWEB) !== 0 ||
          Number(h.CNB) !== 0;

        if (needsPaxSync) {
          onFormChange(`${pathPrefix}.Adults`, { value: Number(values.Adults) });
          onFormChange(`${pathPrefix}.CWEB`, { value: Number(values.Children) });
          onFormChange(`${pathPrefix}.Infants`, { value: Number(values.Infants) });
          onFormChange(`${pathPrefix}.AWEB`, { value: 0 });
          onFormChange(`${pathPrefix}.CNB`, { value: 0 });
        }
      });
    });
  }, [totals, values?.Adults, values?.Children, values?.Infants, values?.PackageOptions, onFormChange, valueGetter]);

  const calcDayOptions = useMemo(() => {
    const options = [];
    for (let i = 1; i <= nights + 1; i++) {
      const date = moment(startDate).add(i - 1, "days");
      options.push({
        label: `Day ${i} (${date.format("DD MMM")})`,
        value: i,
        date: date.toISOString()
      });
    }
    return options;
  }, [startDate, nights]);

  return (
    <FormElement className="p-0">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 px-0">
            <CustomerHeader
              leadID={values.LeadID}
              inquiryID={values.InquiryID}
              values={values}
            />
            <BasicDetailsSection
              adults={values.Adults}
              childrenCount={values.Children}
              infantsCount={values.Infants}
            />
            <PackageCategorySection packageOptions={values.PackageOptions} />
            <PackageOptionSection
              startDate={startDate}
              nights={nights}
              destination={valueGetter("TripDestinationName")}
              tripAdults={values.Adults}
              tripChildren={values.Children}
              tripInfants={values.Infants}
              onFormChange={onFormChange}
              packageTotals={totals.packageOptionsTotals}
            />
            <TransportsAndActivitiesSection
              startDate={startDate}
              values={values}
              valueGetter={valueGetter}
              onFormChange={onFormChange}
              dayOptions={calcDayOptions}
              transportTotal={totals.globalTransportTotal}
            />
            <OtherSpecialServicesSection dayOptions={calcDayOptions} onFormChange={onFormChange} />

            <ShadowCard className="b2b-quotation-module b2b-card mb-4 mt-2">
              <div className="p-4">
                <div className="d-flex align-items-center gap-3 mb-1">
                  {/* <div
                    className="p-2 rounded-circle d-flex align-items-center justify-content-center text-white shadow-sm"
                    style={{ width: "40px", height: "40px", background: "#00bcd4" }} // Teal icon background
                  >
                    <IconComponent icon={FaClipboardList} style={{ fontSize: "18px" }} />
                  </div> */}
                  <div className="hotel-icon-wrap shadow-sm">
                    <IconComponent icon={FaClipboardList} />
                  </div>
                  <div>
                    <Typography.h4 className="fw-bold text-dark m-0" style={{ fontSize: "18px" }}>
                      Inclusions & Exclusions
                    </Typography.h4>
                    <Typography.p className="text-muted m-0" style={{ fontSize: "12px" }}>
                      Define what is included and excluded in this package, along with important notes and terms.
                    </Typography.p>
                  </div>
                </div>

                <div className="row g-4 mt-2">
                  <div className="col-12">
                    <Field
                      name="Inclusion"
                      component={FormRichTextField}
                      label="Inclusions"
                      minHeight={200}
                    />
                  </div>
                  <div className="col-12">
                    <Field
                      name="Exclusion"
                      component={FormRichTextField}
                      label="Exclusions"
                      minHeight={200}
                    />
                  </div>
                  <div className="col-12">
                    <Field
                      name="ImportantNotes"
                      component={FormRichTextField}
                      label="Important Notes"
                      minHeight={200}
                    />
                  </div>
                  <div className="col-12">
                    <Field
                      name="TermsConditions"
                      component={FormRichTextField}
                      label="Terms & Conditions"
                      minHeight={200}
                    />
                  </div>
                </div>
              </div>
            </ShadowCard>

            <B2BTourItinerarySection formRenderProps={formRenderProps} />

            <QuotationSummary values={values} />
            {/* ✅ valueGetter passed so PricingAndSaveSection reads live markup fields */}
            <PricingAndSaveSection
              values={values}
              valueGetter={valueGetter}
              onFormChange={onFormChange}
              packageTotals={totals.packageOptionsTotals}
            />
          </div>
        </div>
      </div>
    </FormElement>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CreateB2BQuotation
// ─────────────────────────────────────────────────────────────────────────────
const CreateB2BQuotation: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const editQuotationID = location.state?.QuotationID;
  const leadID = location.state?.LeadID;
  const inquiryID = location.state?.InquiryID;
  const gridRef = useRef<any>(null);

  const [formInitialValues, setFormInitialValues] = React.useState(initialValues);
  const [loading, setLoading] = React.useState(!!editQuotationID || !!leadID || !!inquiryID);


  // ── Effect: Hydrate from Lead/Inquiry if provided ──────────────────────────
  useEffect(() => {
    const hydrateFromLeadInquiry = async () => {
      if (editQuotationID) return;

      if (leadID) {
        setLoading(true);
        const res = await dispatch(getLeadByID(leadID));
        if (res.meta.requestStatus === "fulfilled") {
          const l = res.payload;
          setFormInitialValues((prev: any) => ({
            ...prev,
            LeadID: l.ID,
            Initial: l.Initial || "",
            FirstName: l.FirstName || "",
            LastName: l.LastName || "",
            EmailID: l.EmailID || "",
            MobileNo: l.MobileNo || "",
            LeadSourceID: l.LeadSourceID || null,
          }));
        }
        setLoading(false);
      } else if (inquiryID) {
        setLoading(true);
        const res = await dispatch(getInquiryByID(inquiryID));
        if (res.meta.requestStatus === "fulfilled") {
          const i = res.payload;

          // Calculate Pax totals from HotelRooms array
          const hotelRooms = i.HotelRooms || [];
          const totalAdults = hotelRooms.reduce((acc: number, room: any) => acc + (Number(room.Adults) || 0), 0) || Number(i.NoOfPax) || 2;
          const totalChildren = hotelRooms.reduce((acc: number, room: any) => acc + (Number(room.Child) || 0), 0) || 0;
          const totalInfants = hotelRooms.reduce((acc: number, room: any) => acc + (Number(room.Infant) || 0), 0) || 0;

          // Calculate dates
          const startDate = i.TravelDate ? moment(i.TravelDate, "YYYY-MM-DD").toDate() : new Date();
          const nights = Number(i.NoOfNights) || 0;
          const endDate = moment(startDate).add(nights, "days").toDate();

          setFormInitialValues((prev: any) => ({
            ...prev,
            InquiryID: i.ID,
            LeadID: i.LeadID || 0,
            Initial: i.Initial || "",
            FirstName: i.FirstName || "",
            LastName: i.LastName || "",
            EmailID: i.EmailID || "",
            MobileNo: i.MobileNo || "",
            LeadSourceID: i.InquirySourceID || 0,
            TripDestinationID: i.DestinationID || 0,
            TripDestinationName: i.Destination || "",
            Nights: nights,
            TripStartDate: startDate,
            TripEndDate: endDate,
            Adults: totalAdults,
            Children: totalChildren,
            Infants: totalInfants,
          }));
        }
        setLoading(false);
      }
    };
    hydrateFromLeadInquiry();
  }, [leadID, inquiryID, editQuotationID, dispatch]);

  useEffect(() => {
    const fetchQuotationData = async () => {
      if (editQuotationID) {
        setLoading(true);
        const res = await dispatch(findByB2BCustomQuotation(editQuotationID));
        if (res.meta.requestStatus === "fulfilled") {
          const data = res.payload;

          // ── Helper to regenerate stay night labels ─────────────────────────
          const getNightLabel = (n: number, start: Date) => {
            const date = moment(start).add(n - 1, 'days');
            const ordinal = n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
            return `${ordinal} N (${date.format('ddd DD MMM')})`;
          };
          const bStart = data.TripStartDate ? moment(data.TripStartDate, "YYYY-MM-DD").toDate() : new Date();
          const n = Number(data.Nights) || 0;
          const bEnd = moment(bStart).add(n, "days").toDate();

          // ── Map the response back to the form state structure ────────────────
          const hydratedData = {
            ...data,
            TripStartDate: bStart,
            TripEndDate: bEnd,
            QuotationID: editQuotationID,
            PackageOptions: (data.PackageOptions || []).map((opt: any) => ({
              ...opt,
              Hotels: (opt.Hotels || []).map((h: any) => ({
                ...h,
                HotelName: h.HotelName || "",
                RoomTypeName: h.RoomTypeName || "",
                MealPlanName: h.MealPlanName || "",
                // UI expects array of labels like ["1st N (...)"]
                Nights: (h.Nights || []).map((n: any) => getNightLabel(n.Night, bStart)),
                NightPrices: (h.NightPrices || []).map((np: any) => ({
                  ...np,
                  _displayDate: np.Date ? moment(np.Date).format('DD MMM') : "",
                  _displayDay: np.Date ? moment(np.Date).format('ddd') : ""
                }))
              })),
              HotelSpecialInclusions: (opt.HotelSpecialInclusions || []).map((i: any) => ({
                ...i,
                ServiceID: Number(i.Service) || 0,
                ServiceName: i.ServiceName || "",
                HotelName: i.HotelName || "",
                Night: getNightLabel(i.Night, bStart),
                DayNo: Number(i.Night) || 0
              }))
            })),
            TransportsAndActivities: {
              ...data.TransportsAndActivities,
              ServiceGroups: (data.TransportsAndActivities?.ServiceGroups || []).map((group: any) => ({
                ...group,
                Services: (group.Services || []).map((s: any) => ({
                  ...s,
                  ServiceName: s.ServiceName || "",
                  ServiceTypeName: s.ServiceTypeName || "",
                  Subservices: (s.Subservices || []).map((sub: any) => ({
                    ...sub,
                    TypeID: s.Type === "TRANSPORT" ? (Number(sub.TypeID) || 0) : sub.TypeID,
                    TypeName: sub.TypeName || sub.VehicleTypeName || sub.TouristTypeName || ""
                  }))
                }))
              })),
              ExtraTransportationServices: (data.TransportsAndActivities?.ExtraTransportationServices || []).map((e: any) => ({
                ...e,
                ServiceName: e.ServiceName || "",
                // Field name is Day in TransportsAndActivitiesSection.tsx
                Day: Number(e.DayNo) || 0
              }))
            },
            OtherSpecialServices: (data.OtherSpecialServices || []).map((e: any) => ({
              ...e,
              ServiceName: e.ServiceName || "",
              // Field name is DayNo in OtherSpecialServicesSection.tsx
              DayNo: Number(e.DayNo) || 0
            })),
            Inclusion: data.Inclusion || "",
            Exclusion: data.Exclusion || "",
            ImportantNotes: data.ImportantNotes || "",
            TermsConditions: data.TermsConditions || "",
            Itinary: (data.Itinary || []).length > 0
              ? data.Itinary.map((it: any) => ({
                ...it,
                DayNo: Number(it.DayNo) || 1,
                PlaceID: Number(it.PlaceID) || null,
                ItinaryID: Number(it.ItinaryID) || null
              }))
              : initialValues.Itinary
          };

          setFormInitialValues(hydratedData);
        }
        setLoading(false);
      }
    };
    fetchQuotationData();
  }, [editQuotationID, dispatch]);

  // ✅ FETCH ITINERARY TEMPLATES: Fetch all active itineraries on mount
  useEffect(() => {
    dispatch(getAllActiveItinerary());
  }, [dispatch]);

  const handleSubmit = async (data: any) => {
    const formatDate = (d: any) => d ? moment(d).format("YYYY-MM-DD") : null;

    const formatDateByDay = (dayNum: any) => {
      const n = Number(dayNum) || 1;
      return moment(data.TripStartDate).add(n - 1, "days").format("YYYY-MM-DD");
    };

    const extractNightNum = (s: string) => {
      if (typeof s !== "string") return Number(s) || 0;
      const match = s.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    };

    // Snapshot-based calculation for submit (valueGetter not available here)
    const totals = calculateQuotationTotals(data);

    const payload = {
      ID: Number(data.QuotationID) || 0,
      InquiryID: Number(data.InquiryID) || 0,
      LeadID: Number(data.LeadID) || 0,
      Initial: data.Initial || "",
      FirstName: data.FirstName || "",
      LastName: data.LastName || "",
      MobileNo: data.MobileNo || "",
      EmailID: data.EmailID || "",
      LeadSourceID: data.LeadSourceID ? Number(data.LeadSourceID) : null,
      QuotationReference: data.QuotationReference || "",
      TripDestinationID: Number(data.TripDestinationID) || 0,
      Nights: Number(data.Nights) || 0,
      TripStartDate: formatDate(data.TripStartDate),
      TripEndDate: formatDate(data.TripEndDate),
      Adults: Number(data.Adults) || 2,
      Children: Number(data.Children) || 0,
      Infants: Number(data.Infants) || 0,

      PackageOptions: (data.PackageOptions || []).map((opt: any, idx: number) => {
        const t = totals.packageOptionsTotals[idx] || {};
        return {
          OptionName: opt.OptionName || "",
          AccommodationsTotal: t.AccommodationsTotal,
          TransportsTotal: t.TransportsTotal,
          ActivityTotal: t.ActivityTotal,
          ExtraServicesTotal: t.ExtraServicesTotal,
          OverallTotal: t.OverallTotal,
          MarkupType: opt.MarkupType || "Amount",
          MarkupValue: Number(opt.MarkupValue) || 0,
          MarkupAmount: t.MarkupAmount,
          GrandTotal: t.GrandTotal,
          Hotels: (opt.Hotels || []).map((h: any) => ({
            HotelID: Number(h.HotelID) || 0,
            MealPlanID: Number(h.MealPlanID) || 0,
            RoomTypeID: Number(h.RoomTypeID) || 0,
            Nights: (h.Nights || []).map((nightStr: string) => {
              const nightNum = extractNightNum(nightStr);
              return {
                Night: nightNum,
                Date: formatDateByDay(nightNum)
              };
            }),
            NoOfRooms: Number(h.NoOfRooms) || 1,
            Adults: Number(h.Adults) || 2,
            AWEB: Number(h.AWEB) || 0,
            CWEB: Number(h.CWEB) || 0,
            CNB: Number(h.CNB) || 0,
            Comment: h.Comment || "",
            NightPrices: (h.NightPrices || []).map((np: any) => ({
              Date: formatDate(np.Date),
              Rate: Number(np.Rate) || 0,
              GivenPrice: Number(np.GivenPrice) || 0,
              AdultPrice: Number(np.AdultPrice) || 0,
              AWEBPrice: Number(np.AWEBPrice) || 0,
              CWEBPrice: Number(np.CWEBPrice) || 0,
              CNBPrice: Number(np.CNBPrice) || 0,
              OrgAdultPrice: Number(np.OrgAdultPrice) || 0,
              OrgAWEBPrice: Number(np.OrgAWEBPrice) || 0,
              OrgCWEBPrice: Number(np.OrgCWEBPrice) || 0,
              OrgCNBPrice: Number(np.OrgCNBPrice) || 0
            }))
          })),
          HotelSpecialInclusions: (opt.HotelSpecialInclusions || []).map((i: any) => {
            const nightNum = extractNightNum(i.Night);
            return {
              Service: i.ServiceID?.toString() || "",
              HotelID: Number(i.HotelID) || 0,
              Night: nightNum,
              Date: formatDateByDay(nightNum),
              TotalPrice: Number(i.TotalPrice) || 0,
              Comment: i.Comment || ""
            };
          })
        };
      }),

      TransportsAndActivities: {
        // SameCabTypeForAll: data.TransportsAndActivities?.SameCabTypeForAll ?? true,
        ServiceGroups: (data.TransportsAndActivities?.ServiceGroups || []).map((g: any) => ({
          StayDays: (g.StayDays || []).map((sd: any) => ({
            Day: Number(sd.Day) || 0,
            Date: formatDate(sd.Date)
          })),
          Services: (g.Services || []).map((s: any) => ({
            Type: s.Type,
            ServiceID: Number(s.ServiceID) || 0,
            ServiceTypeID: Number(s.ServiceTypeID) || 0,
            Slot: s.Slot || "",
            DurationMinutes: Number(s.DurationMinutes) || 0,
            Comment: s.Comment || "",
            Subservices: (s.Subservices || []).map((sub: any) => ({
              TypeID: `${sub.TypeID}` || "",
              Qty: Number(sub.Qty) || 1,
              DailyPrices: (sub.DailyPrices || []).map((dp: any) => ({
                Date: formatDate(dp.Date),
                Rate: Number(dp.Rate) || 0,
                GivenPrice: Number(dp.GivenPrice) || 0
              }))
            }))
          }))
        })),
        // ✅ Correct nested path — ExtraTransportationServices under TransportsAndActivities
        ExtraTransportationServices: (data.TransportsAndActivities?.ExtraTransportationServices || []).map((e: any) => {
          const dNo = Number(e.DayNo) || Number(e.Day) || 0;
          return {
            ServiceID: Number(e.ServiceID) || 0,
            TotalPrice: Number(e.TotalPrice) || 0,
            Day: formatDateByDay(dNo),
            DayNo: dNo,
            Comment: e.Comment || ""
          };
        })
      },
      OtherSpecialServices: (data.OtherSpecialServices || []).map((os: any) => {
        const dNo = Number(os.DayNo) || 0;
        return {
          ServiceID: Number(os.ServiceID) || 0,
          TotalPrice: Number(os.TotalPrice) || 0,
          Day: formatDateByDay(dNo),
          DayNo: dNo,
          Comment: os.Comment || ""
        };
      }),
      Inclusion: data.Inclusion || "",
      Exclusion: data.Exclusion || "",
      ImportantNotes: data.ImportantNotes || "",
      TermsConditions: data.TermsConditions || "",
      Itinary: (data.Itinary || []).map((it: any) => ({
        DayNo: Number(it.DayNo) || 0,
        PlaceID: Number(it.PlaceID) || 0,
        ItinaryID: Number(it.ItinaryID) || 0,
        Description: it.Description || "",
        ItineraryImage: it.ItineraryImage || "",
        Place: it.Place || ""
      }))
    };

    const action = editQuotationID ? updateB2BPackageQuotation(payload) : insertB2BPackageQuotation(payload);
    const res = await dispatch(action);
    if (res.meta.requestStatus === "fulfilled") {
      navigate("/b2bquotation");
    }
  };

  return (
    // <div className="b2b-quotation-wrapper bg-light min-vh-100 p-3 p-lg-4">
    <>
      {loading ? (
        <LoadingPanel gridRef={gridRef} />
      ) : (
        <Form
          key={editQuotationID || "create"}
          onSubmit={handleSubmit}
          initialValues={formInitialValues}
          render={(props) => <QuotationFormBody {...props} />}
        />
      )}
    </>
    // </div>
  );
};

export default CreateB2BQuotation;